@if (commandPalette.isOpen() || commandPalette.closing()) {
  <div
    class="mo-cmd-backdrop"
    [class.mo-cmd-closing]="commandPalette.closing()"
    role="dialog"
    aria-modal="true"
    aria-label="Command palette"
    (click)="onBackdropClick($event)"
  >
    <div class="mo-cmd-dialog">
      <!-- Search Input -->
      <div class="mo-cmd-header">
        <span class="pi pi-search mo-cmd-search-icon" aria-hidden="true"></span>
        <input
          #searchInput
          type="text"
          class="mo-cmd-input"
          placeholder="Type a command or search..."
          [ngModel]="commandPalette.query()"
          (ngModelChange)="onQueryChange($event)"
          (keydown)="onKeydown($event)"
          autocomplete="off"
          spellcheck="false"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded="true"
          aria-controls="mo-cmd-listbox"
          [attr.aria-activedescendant]="'mo-cmd-item-' + selectedIndex()"
        />
        <kbd class="mo-cmd-kbd" aria-hidden="true">ESC</kbd>
      </div>

      <!-- Current scope — informative only, no click target in this ticket -->
      @if (scopeSegments().length > 0) {
        <div class="mo-cmd-scope">
          <span class="visually-hidden">Current scope:</span>
          @for (segment of scopeSegments(); track segment.kind; let first = $first) {
            @if (!first) {
              <span class="mo-cmd-scope-sep" aria-hidden="true">/</span>
            }
            <span class="mo-cmd-scope-chip" [attr.data-scope]="segment.kind">
              <span class="mo-cmd-scope-icon {{ scopeIcon(segment.kind) }}" aria-hidden="true"></span>
              <span class="mo-cmd-scope-label">{{ segment.label }}</span>
            </span>
          }
        </div>
      }

      <!-- Results -->
      <div class="mo-cmd-results" id="mo-cmd-listbox" role="listbox" [attr.aria-label]="'Commands'" (pointermove)="onPointerMove()">
        <div #resultsInner class="mo-cmd-results-inner">
          <!-- Static Commands -->
          @for (group of commandPalette.groupedCommands(); track group.category; let gi = $index) {
            <div class="mo-cmd-group" role="group" [attr.aria-labelledby]="'mo-cmd-group-' + gi">
              <div class="mo-cmd-group-label" [id]="'mo-cmd-group-' + gi" aria-hidden="true">
                {{ group.category }}
              </div>
              @for (cmd of group.commands; track cmd.id; let ci = $index) {
                <button
                  #cmdItem
                  class="mo-cmd-item"
                  role="option"
                  [id]="'mo-cmd-item-' + getGlobalIndex(gi, ci)"
                  [attr.aria-selected]="getGlobalIndex(gi, ci) === selectedIndex()"
                  [class.mo-cmd-item--selected]="getGlobalIndex(gi, ci) === selectedIndex()"
                  (click)="executeCommand(cmd)"
                  (mouseenter)="onItemHover(getGlobalIndex(gi, ci))"
                >
                  @if (cmd.icon) {
                    <span class="mo-cmd-item-icon {{ cmd.icon }}" aria-hidden="true"></span>
                  }
                  <span class="mo-cmd-item-label">{{ cmd.label }}</span>
                  <!-- Resource commands open the current scope, so the row says which one:
                       "Pods" inside a workspace lists that workspace's pods, not the cluster's. -->
                  @if (cmd.requiresResourceScope && commandPalette.resourceScope(); as scope) {
                    <span class="mo-cmd-type-badge" [attr.data-type]="scope.kind">{{ typeLabel(scope.kind) }}</span>
                    <span class="mo-cmd-resource-meta">
                      <span class="mo-cmd-resource-ns">in {{ scope.name }}</span>
                    </span>
                  }
                  @if (cmd.shortcut) {
                    <kbd class="mo-cmd-item-shortcut" aria-hidden="true">{{ cmd.shortcut }}</kbd>
                  }
                </button>
              }
            </div>
          }

          <!-- Navigation Results (Clusters / Workspaces) -->
          @if (commandPalette.navigationResults().length > 0) {
            <div class="mo-cmd-group" role="group" aria-labelledby="mo-cmd-group-navigation">
              <div class="mo-cmd-group-label" id="mo-cmd-group-navigation" aria-hidden="true">
                Jump to
              </div>
              @for (nav of commandPalette.navigationResults(); track nav.name + nav.type; let ni = $index) {
                <button
                  #cmdItem
                  class="mo-cmd-item mo-cmd-item--has-actions"
                  role="option"
                  [id]="'mo-cmd-item-' + getNavigationGlobalIndex(ni)"
                  [attr.aria-selected]="getNavigationGlobalIndex(ni) === selectedIndex()"
                  [class.mo-cmd-item--selected]="getNavigationGlobalIndex(ni) === selectedIndex()"
                  (click)="executeNavigation(nav)"
                  (mouseenter)="onItemHover(getNavigationGlobalIndex(ni))"
                >
                  <span class="mo-cmd-item-icon {{ nav.icon }}" aria-hidden="true"></span>
                  <span class="mo-cmd-item-label">{{ nav.name }}</span>
                  <!-- Outside mo-cmd-resource-meta on purpose: that block collapses on
                       hover to make room for the quick actions, and the kind must stay
                       readable on the row the user is actually on. -->
                  <span class="mo-cmd-type-badge" [attr.data-type]="nav.type">{{ typeLabel(nav.type) }}</span>
                  @if (nav.parentName) {
                    <span class="mo-cmd-resource-meta">
                      <span class="mo-cmd-resource-ns">in {{ nav.parentName }}</span>
                    </span>
                  }
                  <div class="mo-cmd-actions" (click)="$event.stopPropagation()">
                    @for (action of getNavigationActions(nav); track action.id) {
                      <button
                        class="mo-cmd-action"
                        [title]="action.shortcut ? action.label + ' (' + action.shortcut + ')' : action.label"
                        (click)="executeQuickAction(action, nav, $event)"
                      >
                        <span class="{{ action.icon }}"></span>
                      </button>
                    }
                  </div>
                </button>
              }
            </div>
          }

          <!-- Resource Results -->
          @if (commandPalette.resourceResults().length > 0) {
            <div class="mo-cmd-group" role="group" aria-labelledby="mo-cmd-group-resources">
              <div class="mo-cmd-group-label" id="mo-cmd-group-resources" aria-hidden="true">
                Resources
              </div>
              @for (resource of commandPalette.resourceResults(); track resource.name + resource.namespace + resource.kind; let ri = $index) {
                <button
                  #cmdItem
                  class="mo-cmd-item mo-cmd-item--has-actions"
                  role="option"
                  [id]="'mo-cmd-item-' + getResourceGlobalIndex(ri)"
                  [attr.aria-selected]="getResourceGlobalIndex(ri) === selectedIndex()"
                  [class.mo-cmd-item--selected]="getResourceGlobalIndex(ri) === selectedIndex()"
                  (click)="executeResource(resource)"
                  (mouseenter)="onItemHover(getResourceGlobalIndex(ri))"
                >
                  <span class="mo-cmd-item-icon {{ resource.icon }}" aria-hidden="true"></span>
                  <span class="mo-cmd-item-label">{{ resource.name }}</span>
                  <span class="mo-cmd-resource-meta">
                    <span class="mo-cmd-resource-kind">{{ resource.kind }}</span>
                    @if (resource.namespace) {
                      <span class="mo-cmd-resource-ns">{{ resource.namespace }}</span>
                    }
                  </span>
                  <div class="mo-cmd-actions" (click)="$event.stopPropagation()">
                    @for (action of getResourceActions(resource); track action.id) {
                      <button
                        class="mo-cmd-action"
                        [class.mo-cmd-action--danger]="action.dangerous"
                        [title]="action.shortcut ? action.label + ' (' + action.shortcut + ')' : action.label"
                        (click)="executeQuickAction(action, resource, $event)"
                      >
                        <span class="{{ action.icon }}"></span>
                      </button>
                    }
                  </div>
                </button>
              }
            </div>
          }

          <!-- Loading indicator for resource search -->
          @if (commandPalette.resourceLoading() && commandPalette.query().length >= 2) {
            <div class="mo-cmd-loading">
              <mo-spinner size="xs" [debounce]="false" />
              <span>Searching resources...</span>
            </div>
          }

          <!-- Empty state -->
          @if (!commandPalette.hasResults() && !commandPalette.resourceLoading() && commandPalette.query()) {
            <div class="mo-cmd-empty" role="status">
              <span class="pi pi-search" aria-hidden="true"></span>
              <span>No results for "{{ commandPalette.query() }}"</span>
            </div>
          }
        </div>
      </div>

      <!-- Zero-query hints. Outside the listbox: not an option, so selectedIndex is untouched. -->
      @if (showZeroQueryHints()) {
        <div class="mo-cmd-footer">
          <span class="mo-cmd-footer-hint">
            <kbd aria-hidden="true">&uarr;</kbd>
            <kbd aria-hidden="true">&darr;</kbd>
            <span class="visually-hidden">Arrow keys</span>
            to navigate
          </span>
          <span class="mo-cmd-footer-hint">
            <kbd aria-hidden="true">&crarr;</kbd>
            <span class="visually-hidden">Enter</span>
            to open
          </span>
          <span class="mo-cmd-footer-hint">
            <kbd>esc</kbd>
            to close
          </span>
          <!-- TODO(design): final wording of the hints is open. -->
          <span class="mo-cmd-footer-hint mo-cmd-footer-hint--prefix">
            <kbd>:</kbd>
            for commands and resources
          </span>
        </div>
      }
    </div>
  </div>
}
